import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { createAttachedSignature } from 'crypto-pro';
import { format } from 'date-fns';
import UcpSign from '../components/ucp-sign/ucp-sign';
import { fetchIdOrder } from '../store/actions/order-detailed';
import {
  setSignedDecisionDoc,
  setSigningInfo,
  signAdjustmentDoc,
} from '../store/actions/order-realization-detailed';
import { changedAdjustmentPositions } from '../store/selectors/order-realization-detaild';
import { RootState } from '../types';
import { useSign } from 'hooks';

const SignAdjustedPositionsContainer: React.FC = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const params = useParams<{ id: string }>();
  const adjustment = useSelector(
    (state: RootState) => state.realizationPositions.data.adjustment
  );
  const order = useSelector(
    (state: RootState) => state.orderDetailed.data.order
  );
  const adjustmentDoc = useSelector(
    (state: RootState) => state.realizationPositions.data.adjustmentDoc
  );
  const changedPositions = useSelector(changedAdjustmentPositions);

  const {
    certificatesList,
    selectedCertificate,
    onUserSelectCertificate,
    usersCertificates,
  } = useSign();

  const currentCert = useMemo(() => {
    return usersCertificates.find(
      (certificate) => certificate.thumbprint === selectedCertificate?.value
    );
  }, [usersCertificates, selectedCertificate]);

  useEffect(() => {
    dispatch(fetchIdOrder({ id: parseInt(params.id, 10) }));
  }, []);

  const onClose = () => {
    history.push('/orders');
  };

  const onSubmit = async () => {
    if (currentCert && adjustmentDoc) {
      const signed = await createAttachedSignature(
        currentCert.thumbprint,
        adjustmentDoc
      );
      const binarySigned = new Blob([], {
        type: 'application/pdf',
      });
      dispatch(
        setSigningInfo({
          thumbprint: currentCert.thumbprint,
          name: currentCert.issuerName,
          date: `с ${format(
            new Date(currentCert.validFrom),
            'dd/MM/yyyy'
          )} до ${format(new Date(currentCert.validTo), 'dd/MM/yyyy')}`,
        })
      );
      dispatch(setSignedDecisionDoc(signed));
      dispatch(
        signAdjustmentDoc({
          doc: binarySigned,
        })
      );
      history.push(`/orders/${params.id}/sign/result`, {
        isAdjustment: true,
        success: true,
        signed,
      });
    }
  };

  return (
    <UcpSign
      isAdjustment
      disabled={!selectedCertificate}
      certsOptions={certificatesList}
      onClose={onClose}
      onSubmit={onSubmit}
      detailsList={
        changedPositions?.map((item) => ({
          refNumber: item.title,
          sum: item.sum?.toString() ?? '',
        })) ?? []
      }
      totalSum={adjustment?.difference ?? 0}
      initiatorOrderInfo={{
        inn: order?.initiatorTIN ?? '',
        name: order?.initiator ?? '',
      }}
      recipientOrderInfo={{
        inn: order?.recipientTIN ?? '',
        name: order?.recipient ?? '',
      }}
      onCertSelect={onUserSelectCertificate}
      selectedCert={selectedCertificate}
    />
  );
};

export default SignAdjustedPositionsContainer;
